// SPDX-License-Identifier: AGPL-3.0-or-later

import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {createRoomSpatialAudio, type SpatialSpeaker} from '@app/features/voice_scene/audio/RoomSpatialAudio';
import type {SpatialAudioInput, SpatialListener} from '@app/features/voice_scene/audio/SpatialAudioMath';
import type {RoomPoseSource} from '@app/features/voice_scene/hooks/usePositionSender';
import {RoomPositions} from '@app/features/voice_scene/net/PositionBuffer';
import type {Vec3, VoiceSceneHandle} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import type {SeatAssignment, SeatParticipant} from '@app/features/voice_scene/utils/SeatAssignment';
import type {RoomPose} from '@fluxer/constants/src/RoomCharacters';
import type {Room} from 'livekit-client';
import {useEffect, useRef, useState} from 'react';

// ~0.3°: a zoom or a resize redraws the scene without turning the listener.
const AZIMUTH_EPSILON_RAD = 0.005;
// Walkers move the sound 5 times a second; the panners' own ramp smooths in between.
export const WALK_SYNC_INTERVAL_MS = 200;
// A standing character's mouth and ears above its feet, at the natural ~1.8 m scale.
export const WALKER_HEAD_M = 1.6;

const headOf = (pose: RoomPose): Vec3 => ({x: pose.x, y: pose.y + WALKER_HEAD_M, z: pose.z});

// Who walks right now: my own pose and the remote walkers' interpolated ones (null = in their seat).
interface WalkSnapshot {
	mine: RoomPose | null;
	others: (identity: string) => RoomPose | null;
}

// The listener: my character (walking: its head) or my seat; turned with the camera, or in the eyes view while
// walking with my character's own yaw.
export function roomListener(
	handle: VoiceSceneHandle,
	ownPosition: Vec3 | undefined,
	mine: RoomPose | null,
): SpatialListener {
	const {layout} = handle;
	const home = {camHome: layout.camHome, camTarget: layout.camTarget};
	if (!mine) return {position: ownPosition ?? layout.camTarget, azimuth: handle.getCameraAzimuth(), home};
	const listener: SpatialListener = {position: headOf(mine), azimuth: handle.getCameraAzimuth(), home, walking: true};
	if (handle.getCameraMode() === 'eyes') listener.facing = mine.yaw;
	return listener;
}

interface SpatialParticipant extends SeatParticipant {
	isLocal: boolean;
	hasScreenShare: boolean;
}

function buildInput(
	handle: VoiceSceneHandle,
	assignment: SeatAssignment<SeatParticipant>,
	participants: ReadonlyArray<SpatialParticipant>,
	myUserId: string | null,
	azimuth: number,
	walk: WalkSnapshot | null,
): SpatialAudioInput {
	const {layout} = handle;
	const seatRank = new Map(layout.seats.map((seat, index) => [seat.name, index]));
	const rank = (userId: string): number => {
		const seat = assignment.placements.get(userId)?.seat;
		return seat ? (seatRank.get(seat) ?? layout.seats.length) : layout.seats.length;
	};
	// Seat order, then standing in list order (stable sort): which four get HRTF stays put as people talk.
	const remote = participants.filter((participant) => !participant.isLocal);
	const ordered = remote.map((participant, index) => ({participant, index}));
	ordered.sort((a, b) => rank(a.participant.userId) - rank(b.participant.userId) || a.index - b.index);
	const speakers: Array<SpatialSpeaker> = [];
	for (const {participant} of ordered) {
		const placement = assignment.placements.get(participant.userId);
		if (!placement) continue;
		const identity = buildVoiceParticipantIdentity(participant.userId, participant.connectionId);
		const pose = walk?.others(identity) ?? null;
		speakers.push(
			pose
				? {identity, kind: 'mic', position: headOf(pose), walking: true}
				: {identity, kind: 'mic', position: placement.position},
		);
	}
	if (layout.screen) {
		for (const participant of remote) {
			if (!participant.hasScreenShare) continue;
			speakers.push({
				identity: buildVoiceParticipantIdentity(participant.userId, participant.connectionId),
				kind: 'screen',
				position: layout.screen.center,
			});
		}
	}
	const own = myUserId ? assignment.placements.get(myUserId) : undefined;
	// home: azimuth 0 is the cam_home view; without it the sound is turned 20–34° from the picture.
	const listener = roomListener(handle, own?.position, walk?.mine ?? null);
	listener.azimuth = azimuth;
	return {listener, speakers};
}

// Binds the room view to 3D audio: seats and the camera steer each speaker's panner; the view closing removes the chains.
// walk: my pose source while walking is on in this room (null: off, everyone is heard from their seat as before);
// walkers are then heard where they walk, with the distance curve.
export function useSpatialAudioSync({
	handle,
	room,
	assignment,
	participants,
	myUserId,
	enabled,
	walk = null,
	walkActive = true,
}: {
	handle: VoiceSceneHandle;
	room: Room | null;
	assignment: SeatAssignment<SeatParticipant>;
	participants: ReadonlyArray<SpatialParticipant>;
	myUserId: string | null;
	enabled: boolean;
	walk?: RoomPoseSource | null;
	// Whether anyone walks now: the walking timer runs only then.
	walkActive?: boolean;
}): void {
	const [controller] = useState(() => createRoomSpatialAudio());
	const lastAzimuth = useRef(0);
	const snapshot = (): WalkSnapshot | null =>
		walk ? {mine: walk.getPose(), others: (identity) => RoomPositions.pose(identity, performance.now())} : null;
	const build = useRef((azimuth: number) =>
		buildInput(handle, assignment, participants, myUserId, azimuth, snapshot()),
	);
	build.current = (azimuth) => buildInput(handle, assignment, participants, myUserId, azimuth, snapshot());

	useEffect(() => () => controller.detach(), [controller]);
	useEffect(() => {
		if (room) controller.attach(room);
		else controller.detach();
	}, [controller, room]);
	useEffect(() => {
		controller.setMode(enabled ? 'spatial' : 'flat');
	}, [controller, enabled]);
	useEffect(() => {
		lastAzimuth.current = handle.getCameraAzimuth();
		controller.update(build.current(lastAzimuth.current));
	}, [controller, handle, assignment, participants, myUserId]);
	useEffect(() => {
		if (!walk || !walkActive) return;
		let walked = false;
		const interval = setInterval(() => {
			const input = build.current(lastAzimuth.current);
			const walking = input.listener.walking === true || input.speakers.some((speaker) => speaker.walking);
			// One more update after the last walker sits puts everyone back on their seats; then nothing until the next.
			if (walking || walked) controller.update(input);
			walked = walking;
		}, WALK_SYNC_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [controller, walk, walkActive]);
	useEffect(
		() =>
			handle.onViewChange(() => {
				const azimuth = handle.getCameraAzimuth();
				if (Math.abs(azimuth - lastAzimuth.current) < AZIMUTH_EPSILON_RAD) return;
				lastAzimuth.current = azimuth;
				controller.update(build.current(azimuth));
			}),
		[controller, handle],
	);
}
